import { useState } from 'react';

/**
 * A document's note, split into what a PERSON wrote and the lines the system
 * adds on its own ("POS sale POS-000123", "POS terminal · Cash · till name",
 * "Converted from order SO-000042" — which other services find the invoice by).
 * The note box on the printout edits only the first part, and saving puts the
 * system lines back, so keeping a note never loses the till's own trail.
 */
export const SYSTEM_NOTE = /^(POS (sale \S+|terminal ·.*)|Converted from order \S+)$/;

export function splitNote(notes: string | null | undefined): { user: string; system: string } {
  const lines = (notes ?? '').split(/\r?\n/);
  return {
    user: lines.filter((l) => l.trim() && !SYSTEM_NOTE.test(l.trim())).join('\n'),
    system: lines.filter((l) => SYSTEM_NOTE.test(l.trim())).join('\n'),
  };
}

/** What a person wrote on the document — what a list shows in its Notes column. */
export const userNote = (notes: string | null | undefined): string => splitNote(notes).user;

/**
 * A list's "show notes" switch, remembered per list in this browser. Off until
 * someone wants it: most lists are read for numbers and money, and a column of
 * free text widens every row.
 */
export function useShowNotes(list: string): [boolean, (on: boolean) => void] {
  const key = `erp-list-notes:${list}`;
  const [on, setOn] = useState(() => {
    try { return window.localStorage.getItem(key) === '1'; } catch { return false; }
  });
  const set = (next: boolean): void => {
    setOn(next);
    try { window.localStorage.setItem(key, next ? '1' : '0'); } catch { /* the in-memory switch still works */ }
  };
  return [on, set];
}
